import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import type { CMPCustomFilter, Filters } from '../../../common/types';
import HiddenCheckbox from './hidden-checkbox';
import Label from './label';

type ConditionalTooltipProps = {
	children: React.ReactNode;
	content: string;
	showTooltip: boolean;
};

const ConditionalTooltip = ({ children, content, showTooltip }: ConditionalTooltipProps) =>
	showTooltip ? (
		<Tooltip content={content} position="bottom">
			{children}
		</Tooltip>
	) : (
		children
	);

type Props = {
	onChange: (filters: Filters, analyticsEvent: UIAnalyticsEvent) => void;
	selectedFilters: Set<string>;
	filter: CMPCustomFilter;
};

const FilterOption = (props: Props) => {
	const { onChange, selectedFilters, filter } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isSelected = useMemo(() => selectedFilters.has(filter.id), [selectedFilters, filter]);

	const onClick = useCallback(() => {
		if (isSelected) {
			selectedFilters.delete(filter.id);
		} else {
			selectedFilters.add(filter.id);
		}

		onChange({ CUSTOM_FILTER: Array.from(selectedFilters) }, createAnalyticsEvent({}));
	}, [onChange, filter, isSelected, selectedFilters, createAnalyticsEvent]);

	const id = `checkbox-id-${filter.name}`;

	const labelRef = useRef<HTMLLabelElement>(null);
	const [showTooltip, setShowTooltip] = useState(false);
	useEffect(() => {
		if (ff('show-quick-filter-tooltip-only-on-truncated-buttons_d8i9d')) {
			if (labelRef.current) {
				const innerSpan = labelRef.current.children.item(0);
				if (innerSpan) {
					setShowTooltip(innerSpan.clientWidth < innerSpan.scrollWidth);
				}
			}
		} else {
			setShowTooltip(true);
		}
	}, [filter.name]);

	return (
		<ConditionalTooltip content={filter.name} showTooltip={showTooltip}>
			<Box xcss={maxWidthWrapperStyles}>
				<HiddenCheckbox id={id} checked={isSelected} onChange={onClick} />
				<Button id={id} isSelected={isSelected} component={Label} ref={labelRef}>
					{filter.name}
				</Button>
			</Box>
		</ConditionalTooltip>
	);
};

export default FilterOption;

const maxWidthWrapperStyles = xcss({
	maxWidth: '380px',
});
